import React from "react";
import { Link } from "react-router-dom";
import PublicNav from "@/components/vansha/PublicNav";
import { motion } from "framer-motion";
import { Heart, Baby, Bookmark, Users, Sparkles, ShieldCheck } from "lucide-react";

const GROUPS = [
  { title: "Pregnancy operating system", Icon: Heart,       accent: "vermilion", features: ["40-week intelligence", "Symptom & wellness tracker with trends", "Medications & dose reminders", "Nutrition & hydration logging", "Medical records vault", "Appointments & scan calendar"] },
  { title: "Baby & beyond",              Icon: Baby,        accent: "marigold",  features: ["Digital baby twin", "Milestone tracking", "Vaccination schedule (global standards)", "Newborn feed/sleep/diaper logs", "Growth charts", "Development guidance"] },
  { title: "Memory & legacy",            Icon: Bookmark,    accent: "sage",      features: ["Family timeline auto-populated", "Photo & video vault (unlimited)", "Letters to baby (time-capsule)", "Daily journal with prompts", "Yearbook PDF & printed hardcover", "Public family journey share links"] },
  { title: "Family & community",         Icon: Users,       accent: "vermilion", features: ["Family circle with permissions", "Private family feed (social)", "Shared task board", "Father & Grandparent modes", "Anonymous community groups", "Emergency contacts vault"] },
  { title: "AI companion",               Icon: Sparkles,    accent: "marigold",  features: ["6 tuned personas (Mother, Father, Sleep, Nutrition, Parenting, Vansha)", "Family-context aware", "AI photo captioning (vision)", "Smart daily prompts", "Auto memory grouping by people", "Journal reflection nudges"] },
  { title: "Preparation & care",         Icon: ShieldCheck, accent: "sage",      features: ["Hospital readiness center", "Birth plan builder", "Pre-conception cycle tracking", "Daily wellness check-ins", "Delivery-day emergency mode", "Concierge callbacks (Care+)"] },
];

const ACCENT = {
  vermilion: { bg: "bg-[#E8552B]/12", fg: "text-[#E8552B]", dot: "bg-[#E8552B]" },
  marigold:  { bg: "bg-[#F4A72C]/16", fg: "text-[#F4A72C]", dot: "bg-[#F4A72C]" },
  sage:      { bg: "bg-[#7BA098]/20", fg: "text-[#7BA098]", dot: "bg-[#7BA098]" },
};

export default function Features() {
  return (
    <div className="min-h-screen bg-[#FBF4E8]" data-testid="features-page">
      <PublicNav />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#F4A72C] mb-6">Everything Vansha does</p>
        <h1 className="font-serif font-semibold text-5xl lg:text-6xl leading-[1.05] max-w-3xl text-[#1B1533]" data-testid="features-title">Fifty-plus features.<br /><em className="text-[#E8552B]">One calm surface.</em></h1>
        <p className="mt-6 text-lg text-[#3D2E5C] max-w-2xl">Every feature earns its place through guidance, memory, family or care. Nothing else made the cut.</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid md:grid-cols-2 gap-8">
          {GROUPS.map((g, i) => {
            const a = ACCENT[g.accent];
            return (
              <motion.div
                key={g.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 2) * 0.1 }}
                className="bg-white border border-[#F5E7CE] rounded-3xl p-10 hover:-translate-y-1 hover:shadow-lg transition-transform"
                data-testid={`feature-group-${i}`}
              >
                <div className={`w-14 h-14 rounded-2xl ${a.bg} ${a.fg} flex items-center justify-center mb-6`}>
                  <g.Icon strokeWidth={1.5} size={26} />
                </div>
                <h2 className="font-serif text-3xl mb-6 text-[#1B1533]">{g.title}</h2>
                <ul className="space-y-3">
                  {g.features.map((f) => (
                    <li key={f} className="flex gap-3 text-[#3D2E5C]">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${a.dot} flex-shrink-0 mt-2.5`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="bg-[#1B1533] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center">
          <h2 className="font-serif font-semibold text-4xl lg:text-5xl leading-tight max-w-3xl mx-auto">All included. <em className="text-[#E8552B]">All beautiful.</em></h2>
          <p className="mt-6 opacity-80 max-w-xl mx-auto">Start free — upgrade to Plus, Care or Signature whenever your family needs more.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/register" className="bg-[#F4A72C] text-[#1B1533] rounded-full px-8 py-4 font-medium hover:scale-105 transition-transform" data-testid="features-cta">Begin free</Link>
            <Link to="/pricing" className="rounded-full px-8 py-4 font-medium border border-white/30 hover:border-white transition" data-testid="features-pricing">See pricing</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
